import type { HelmInspect } from '@features/fields/api/useHelmInspect'
import { BoundaryHoleBehavior, HeadlandCornerType } from 'api-validations/locutus/guidance'
import { createPortal } from 'react-dom'
import type { UseFormReturn } from 'react-hook-form'
import { useWatch } from 'react-hook-form'
import { useLocalization } from 'shared-ui/localization'
import { planZoneColor } from 'shared-ui/map'

import { useCornerOptions } from './Corners'
import { usePassableCorridorOptions } from './Corridors'
import type { FormValues } from './formtypes'
import { cornerKey, corridorKey, edgeKey, holeKey, withOverride } from './overrideKeys'
import { HeadlandCountInput, type PillOption, PillToggle } from './shared'

const FeatureColorDot = ({ color }: { color: string | undefined }) => {
  if (color === undefined) return null
  return (
    <span
      className="h-3 w-3 shrink-0 rounded-full"
      style={{ backgroundColor: color }}
      aria-hidden
    />
  )
}

export type OverridePopupState = {
  // Every same-kind feature the click hit, so overlapping edges/corners each
  // get their own control in one popup.
  featureIds: string[]
  kind: string
  clientX: number
  clientY: number
}

const EdgeContent = ({
  featureId,
  form,
  inspect
}: {
  featureId: string
  form: UseFormReturn<FormValues>
  inspect: HelmInspect | undefined
}) => {
  const { t } = useLocalization()
  const edges = inspect?.edges ?? []
  const edgeIndex = edges.findIndex((e) => e.id === featureId)
  const color = edgeIndex >= 0 ? planZoneColor(edgeIndex) : undefined
  const edgeHeadlands = useWatch({ control: form.control, name: 'edgeHeadlands' })
  const globalCount = useWatch({ control: form.control, name: 'numberOfHeadlands' })
  const edge = edges.at(edgeIndex)
  if (edgeIndex < 0 || edge === undefined) return null
  const key = edgeKey(edge)
  const count = edgeHeadlands.find((o) => o.key === key)?.count
  const isOverridden = count !== undefined

  const setCount = (raw: string) => {
    form.setValue(
      'edgeHeadlands',
      withOverride(edgeHeadlands, key, raw === '' ? undefined : { key, count: Number(raw) }),
      { shouldDirty: true }
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FeatureColorDot color={color} />
          <span className="pan-title5">{t(($) => $.planning.numberOfHeadlands)}</span>
        </div>
        {isOverridden && (
          <button
            type="button"
            onClick={() => setCount('')}
            className="text-xs text-gray-400 underline hover:text-gray-600"
          >
            {t(($) => $.planning.resetToGlobal)}
          </button>
        )}
      </div>
      <HeadlandCountInput
        count={count}
        globalCount={globalCount}
        className="w-full rounded-lg border border-gray-200 text-center text-sm"
        onChange={setCount}
      />
    </div>
  )
}

const CornerContent = ({
  featureId,
  form,
  inspect
}: {
  featureId: string
  form: UseFormReturn<FormValues>
  inspect: HelmInspect | undefined
}) => {
  const { t } = useLocalization()
  const cornerOverrides = useWatch({ control: form.control, name: 'cornerOverrides' })
  const globalCornerType = useWatch({ control: form.control, name: 'cornerType' })
  const cornerOptions = useCornerOptions()
  const corners = inspect?.corners ?? []
  const cornerIndex = corners.findIndex((c) => c.id === featureId)
  const color = cornerIndex >= 0 ? planZoneColor(cornerIndex) : undefined
  const corner = corners.at(cornerIndex)
  if (cornerIndex < 0 || corner === undefined) return null
  const key = cornerKey(corner)
  const override = cornerOverrides.find((o) => o.key === key)?.cornerType
  const isOverridden = override !== undefined

  const setCornerType = (type: HeadlandCornerType | null) => {
    form.setValue(
      'cornerOverrides',
      withOverride(cornerOverrides, key, type === null ? undefined : { key, cornerType: type }),
      { shouldDirty: true }
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FeatureColorDot color={color} />
          <span className="pan-title5">{t(($) => $.planning.corners)}</span>
        </div>
        {isOverridden && (
          <button
            type="button"
            onClick={() => setCornerType(null)}
            className="text-xs text-gray-400 underline hover:text-gray-600"
          >
            {t(($) => $.planning.resetToGlobal)}
          </button>
        )}
      </div>
      <PillToggle
        value={override ?? globalCornerType}
        options={cornerOptions}
        onChange={setCornerType}
        inherited={!isOverridden}
      />
    </div>
  )
}

const HoleContent = ({
  featureId,
  form,
  inspect
}: {
  featureId: string
  form: UseFormReturn<FormValues>
  inspect: HelmInspect | undefined
}) => {
  const { t } = useLocalization()
  const holes = inspect?.holes ?? []
  const holeIndex = holes.findIndex((h) => h.id === featureId)
  const color = holeIndex >= 0 ? planZoneColor(holeIndex) : undefined
  const holeOverrides = useWatch({ control: form.control, name: 'holeOverrides' })
  const globalBehavior = useWatch({ control: form.control, name: 'boundaryHoleBehavior' })
  const hole = holes.at(holeIndex)
  if (holeIndex < 0 || hole === undefined) return null
  const key = holeKey(hole)
  const override = holeOverrides.find((o) => o.key === key)?.passable
  const isOverridden = override !== undefined
  const globalPassable = globalBehavior === BoundaryHoleBehavior.Passable

  const passableOptions: readonly [PillOption<boolean>, PillOption<boolean>] = [
    {
      value: true,
      label: t(($) => $.planning.passable),
      subtleColor: 'bg-green-50 text-green-600',
      strongColor: 'bg-green-100 text-green-800'
    },
    {
      value: false,
      label: t(($) => $.planning.impassable),
      subtleColor: 'bg-red-50 text-red-500',
      strongColor: 'bg-red-100 text-red-700'
    }
  ]

  const setPassable = (passable: boolean | null) => {
    form.setValue(
      'holeOverrides',
      withOverride(holeOverrides, key, passable === null ? undefined : { key, passable }),
      { shouldDirty: true }
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FeatureColorDot color={color} />
          <span className="pan-title5">{t(($) => $.planning.holes)}</span>
        </div>
        {isOverridden && (
          <button
            type="button"
            onClick={() => setPassable(null)}
            className="text-xs text-gray-400 underline hover:text-gray-600"
          >
            {t(($) => $.planning.resetToGlobal)}
          </button>
        )}
      </div>
      <PillToggle
        value={override ?? globalPassable}
        options={passableOptions}
        onChange={setPassable}
        inherited={!isOverridden}
      />
    </div>
  )
}

const CorridorContent = ({
  featureId,
  form,
  inspect
}: {
  featureId: string
  form: UseFormReturn<FormValues>
  inspect: HelmInspect | undefined
}) => {
  const { t } = useLocalization()
  const corridors = inspect?.corridors ?? []
  const corridorIndex = corridors.findIndex((c) => c.id === featureId)
  const color = corridorIndex >= 0 ? planZoneColor(corridorIndex) : undefined
  const corridorOverrides = useWatch({ control: form.control, name: 'corridorOverrides' })
  const passableOptions = usePassableCorridorOptions()
  const corridor = corridors.at(corridorIndex)
  if (corridorIndex < 0 || corridor === undefined) return null
  const key = corridorKey(corridor)
  const passable = corridorOverrides.find((o) => o.key === key)?.passable ?? null
  const isOverridden = passable !== null

  const setPassable = (next: boolean | null) => {
    form.setValue(
      'corridorOverrides',
      withOverride(corridorOverrides, key, next === null ? undefined : { key, passable: next }),
      { shouldDirty: true }
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FeatureColorDot color={color} />
          <span className="pan-title5">{t(($) => $.planning.corridors)}</span>
        </div>
        {isOverridden && (
          <button
            type="button"
            onClick={() => setPassable(null)}
            className="text-xs text-gray-400 underline hover:text-gray-600"
          >
            {t(($) => $.planning.keepSeparate)}
          </button>
        )}
      </div>
      <PillToggle value={passable} options={passableOptions} onChange={setPassable} />
    </div>
  )
}

export const OverrideMapPopup = ({
  popup,
  form,
  inspect,
  onClose
}: {
  popup: OverridePopupState
  form: UseFormReturn<FormValues>
  inspect: HelmInspect | undefined
  onClose: () => void
}) => {
  const { featureIds, kind, clientX, clientY } = popup

  return createPortal(
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div
        className="fixed z-50 flex w-52 flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3 shadow-lg"
        style={{ top: clientY, left: clientX, transform: 'translate(-50%, calc(-100% - 10px))' }}
      >
        {featureIds.map((featureId, index) => (
          <div key={featureId} className={index > 0 ? 'border-t border-gray-100 pt-3' : undefined}>
            {kind === 'edge' && <EdgeContent featureId={featureId} form={form} inspect={inspect} />}
            {kind === 'corner' && (
              <CornerContent featureId={featureId} form={form} inspect={inspect} />
            )}
            {kind === 'hole' && <HoleContent featureId={featureId} form={form} inspect={inspect} />}
            {kind === 'corridor' && (
              <CorridorContent featureId={featureId} form={form} inspect={inspect} />
            )}
          </div>
        ))}
      </div>
    </>,
    document.body
  )
}
